import _upperFirst from "lodash/upperFirst";
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { useUdeForm } from 'app/views/boards/panels/ude/useUdeForm';
import React, { useCallback, useContext } from 'react';
import { observer, MobXProviderContext } from 'mobx-react';
import { Box, Flex, Text } from 'core/components';
import VariableTag from 'app/views/boards/VariableTag';
import { getMeasurementDimension, getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import { addDirectionPrefixToLabel, isLabelDimension, splitBidirectionalKey } from '@kentik/ui-shared/query/dimensionUtils';
import { CUSTOM_FILTER_REGEX } from '@kentik/ui-shared/util/regex';
import styled from 'styled-components';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import { getDimensionDisplayLabel } from '@kentik/ui-shared/query/query';
import { DNS_CODE_FILTER_OPTIONS, HEALTH_FILTER_OPTIONS, HTTP_COMBINED_FILTER_OPTIONS, isFieldComparisonOperator } from './filterConstants';
import ToggleFilterButton from './ToggleFilterButton';

/**
 * Parse metric filter value to extract numeric value and unit
 * Format: "123|unit" where unit is optional
 * Also handles categorical values (health, dns_code, http_status)
 */
function parseMetricFilterValue(value) {
  if (!value) {
    return {
      numericValue: '',
      unit: ''
    };
  }
  const parts = value.split('|');
  return {
    numericValue: parts[0] || '',
    unit: parts[1] || ''
  };
}

/**
 * Get display label for categorical metric values
 */
function getCategoricalValueLabel(value, unit) {
  if (!value) {
    return value;
  }

  // Health status
  if (unit === 'health') {
    const option = HEALTH_FILTER_OPTIONS.find(opt => opt.value === value);
    return option?.label || value;
  }

  // DNS codes
  if (unit === 'dns_code') {
    const option = DNS_CODE_FILTER_OPTIONS.find(opt => opt.value === value);
    return option?.label || value;
  }

  // HTTP status - could be multi-value comma-separated
  if (unit === 'http_status_code') {
    const values = value.split(',').filter(Boolean);
    const labels = values.map(v => {
      const option = HTTP_COMBINED_FILTER_OPTIONS.find(opt => opt.value === v);
      return option?.label || v;
    });
    return labels.join(', ');
  }
  return value;
}
/**
 * Get display value for a filter based on type (metric vs dimension vs field comparison)
 */
function getFilterDisplayValue(filter, getValueLabel, getDimensionLabel) {
  // Field comparison filter - show dimension label for right field
  if (filter.rightFilterField) {
    return getDimensionLabel(filter.rightFilterField);
  }

  // Dimension filter - use value label resolver
  if (!filter.metric) {
    return getValueLabel(filter.filterField, filter.filterValue) || '---';
  }

  // Categorical metric filter (health, DNS, HTTP)
  const categoricalUnits = ['health', 'dns_code', 'http_status_code'];
  if (filter.metricUnitType && categoricalUnits.includes(filter.metricUnitType)) {
    return getCategoricalValueLabel(filter.filterValue, filter.metricUnitType);
  }

  // Numeric metric filter with optional unit suffix
  return `${filter.filterValue}${filter.unit ? ` ${filter.unit}` : ''}`;
}
const FilterWrapper = styled.div.withConfig({
  displayName: "UdeFilterDisplay__FilterWrapper",
  componentId: "sc-jhx5we-0"
})(["&&{&:has(.group:nth-child(2)){padding-left:8px;border-left:2px solid ", ";}}"], ({
  theme
}) => theme.borderColors.thick);
const FilterGroupWrapper = styled.div.withConfig({
  displayName: "UdeFilterDisplay__FilterGroupWrapper",
  componentId: "sc-jhx5we-1"
})(["&&{&:not(:last-child){margin-bottom:8px;}.toggle{visibility:hidden;&.toggle-off{visibility:visible;}}&:hover .toggle{visibility:visible;}&.", "{.group-description{opacity:0.35;}}.filter{padding-left:8px;margin-left:2px;border-left:2px solid ", ";&.", "{>:not(.toggle){opacity:0.35;}}}}"], Classes.DISABLED, ({
  theme
}) => theme.borderColors.thick, Classes.DISABLED);
export const UdeFilterDisplay = observer(({
  filters,
  type,
  measurementName,
  isReadOnly,
  onFilterGroupToggle,
  onFilterItemToggle
}) => {
  const {
    $syn,
    $dictionary,
    $labels
  } = useContext(MobXProviderContext);
  const isMetricMode = type === 'metrics';
  const {
    measurement
  } = useDictionaryMeasurement(measurementName);

  /**
   * Get label for a metric field
   */
  const getMetricLabel = useCallback((aggregate, metricName) => {
    const aggLabel = _upperFirst(aggregate);
    if (!metricName || !measurement) {
      return `${aggLabel} ${metricName}`.trim();
    }
    const metricInfo = getMeasurementMetric(measurement, metricName);
    const baseLabel = metricInfo?.Label || metricName;
    const label = metricInfo?.Window === 'count' ? getDimensionDisplayLabel(metricName, baseLabel) : baseLabel;
    return `${aggLabel} ${label}`.trim();
  }, [measurement]);

  /**
   * Get a friendly display label for a filter value based on the dimension type.
   * For test_id, agent IDs, etc., resolve to their names.
   */
  const getValueLabel = useCallback((filterField, filterValue) => {
    if (!filterValue || !measurement) {
      return filterValue;
    }

    // First check dictionary options - most common case
    const dict = $dictionary;
    const dictionaryOptions = dict.filterFieldValueOptions?.[filterField];
    if (dictionaryOptions) {
      const option = dictionaryOptions.find(opt => opt.value === filterValue);
      if (option) {
        return option.label;
      }
    }

    // Handle test_id - resolve to test name
    if (filterField === 'test_id' && $syn?.tests) {
      const test = $syn.tests.get(filterValue);
      if (test && typeof test.get === 'function') {
        const displayName = test.get('display_name');
        if (displayName) {
          return String(displayName);
        }
      }
    }

    // Handle agent dimensions - resolve to agent name using label computed property
    if ((filterField === 'src_agent' || filterField === 'dst_agent') && $syn?.agents) {
      const agent = $syn.agents.get(filterValue);
      if (agent) {
        // Use the label computed property which returns agent_alias or fallback
        const {
          label
        } = agent;
        if (label) {
          return label;
        }
        // Fallback to agent_alias via get()
        if (typeof agent.get === 'function') {
          const alias = agent.get('agent_alias');
          if (alias) {
            return String(alias);
          }
        }
      }
    }

    // Handle label dimensions - resolve label ID to label name
    if (isLabelDimension(filterField) && $labels) {
      const options = $labels.getLabelOptionsFromIds([Number(filterValue)]);
      if (options.length > 0) {
        return String(options[0].label);
      }
    }
    const dimData = getMeasurementDimension(measurement, filterField);
    if (isEnumItem(dimData)) {
      const enumLabel = dimData.Values[filterValue];
      if (enumLabel) {
        return enumLabel;
      }
    }
    return filterValue;
  }, [measurement, $syn, $dictionary, $labels]);

  /**
   * For label dimension filters, find the label option to pass to LabelList.
   */
  const getLabelForFilter = useCallback((filterField, filterValue) => {
    if (!isLabelDimension(filterField) || !$labels) {
      return null;
    }
    const options = $labels.getLabelOptionsFromIds([Number(filterValue)]);
    if (options.length > 0) {
      const opt = options[0];
      return {
        id: filterValue,
        name: String(opt.label || filterValue),
        bg: String(opt.bg || opt.color || ''),
        color: '#ffffff'
      };
    }
    return null;
  }, [$labels]);
  const getDimensionLabel = useCallback(filterField => {
    if (!filterField || !measurement) {
      return filterField;
    }
    const biParts = splitBidirectionalKey(filterField);
    if (biParts) {
      const dimData = getMeasurementDimension(measurement, biParts.srcVariant) || getMeasurementDimension(measurement, biParts.dstVariant);
      if (dimData?.Label) {
        const baseLabel = dimData.Label.replace(/^(Source|Destination|Src|Dst|Input|Output)\s+/i, '');
        return `Source or Dest ${baseLabel}`;
      }
      return filterField;
    }

    // Look for the dimension in the measurement's Dimensions
    const dimData = getMeasurementDimension(measurement, filterField);
    const label = dimData?.Label || filterField;

    // For directional dimensions, add direction prefix if not already present
    return addDirectionPrefixToLabel(filterField, label);
  }, [measurement]);
  const getOperatorLabel = useCallback(operator => {
    const dict = $dictionary;
    const filterOperators = dict?.dictionary?.queryFilters?.filterOperators || {};
    return filterOperators[operator] || operator;
  }, [$dictionary]);
  const {
    filterGroups
  } = filters;
  const getGroups = () => {
    if (!filterGroups) {
      return [];
    }

    // Check if there are actual filter groups with values
    if (!Array.isArray(filterGroups) || filterGroups.length === 0) {
      return [];
    }
    const result = [];
    filterGroups.forEach((group, groupIndex) => {
      const filtersInfo = [];
      group.filters.map((filter, filterIndex) => {
        const {
          filterField,
          operator,
          disabled = false
        } = filter;
        const isFieldComparison = isFieldComparisonOperator(operator);

        // For field comparison, get rightFilterField instead of filterValue
        const filterValue = isFieldComparison ? filter.rightFilterField || '' : filter.filterValue;
        if (isMetricMode) {
          // Metric filter - get metric name and parse value with unit
          const metricName = 'metric' in filter && filter.metric || filterField;
          const agg = 'aggregate' in filter && filter.aggregate || '';
          const metricInfo = metricName && measurement ? getMeasurementMetric(measurement, metricName) : null;
          const metricUnitType = metricInfo?.Unit || '';

          // Check if this is a categorical filter (no | separator)
          const isCategorical = ['health', 'dns_code', 'http_status_code'].includes(metricUnitType);
          if (isCategorical) {
            // Categorical filter - value is stored directly, no unit suffix
            if (metricName && filterValue) {
              filtersInfo.push({
                filterIndex,
                filterField,
                operator,
                disabled,
                filterValue,
                label: getMetricLabel(agg, metricName),
                metric: metricName,
                metricUnitType
              });
            }
          } else {
            // Numeric filter - parse value with unit
            const {
              numericValue,
              unit
            } = parseMetricFilterValue(filterValue);
            if (metricName && numericValue) {
              filtersInfo.push({
                filterIndex,
                filterField,
                operator,
                disabled,
                filterValue: numericValue,
                label: getMetricLabel(agg, metricName),
                metric: metricName,
                unit,
                metricUnitType
              });
            }
          }
        } else if (filterField && (!isFieldComparison || filterValue)) {
          const filterInfo = {
            filterIndex,
            filterField,
            operator,
            disabled,
            filterValue,
            label: getDimensionLabel(filterField)
          };

          // For field comparison, store the right field dimension
          if (isFieldComparison) {
            filterInfo.rightFilterField = filterValue;
          }
          filtersInfo.push(filterInfo);
        }
        return null;
      });
      if (filtersInfo.length > 0) {
        result.push({
          groupIndex,
          filters: filtersInfo,
          not: group.not,
          connector: group.connector,
          disabled: group.disabled || false
        });
      }
    });
    return result;
  };
  const groups = getGroups();
  const topConnectorValue = filters.connector;
  return /*#__PURE__*/React.createElement(Box, {
    pb: "2px"
  }, groups.length > 1 && /*#__PURE__*/React.createElement(Text, {
    small: true,
    mb: "4px",
    as: "div"
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: 600
  }, topConnectorValue === 'All' ? 'All' : 'Any'), " of the following:"), /*#__PURE__*/React.createElement(FilterWrapper, null, groups.map(group => {
    const isSingularFilter = group.filters.length === 1;
    const includeExclude = group.not ? 'Exclude' : 'Include';
    const allAny = group.connector === 'All' ? 'all' : 'any';
    return /*#__PURE__*/React.createElement(FilterGroupWrapper, {
      key: `group-${group.connector}-${group.not}-${group.filters.map(f => f.filterField).join('-')}`,
      className: classNames('group', {
        [Classes.DISABLED]: group.disabled
      })
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      gap: "6px"
    }, /*#__PURE__*/React.createElement(Text, {
      className: "group-description",
      small: true
    }, /*#__PURE__*/React.createElement(Text, {
      fontWeight: 600
    }, includeExclude), " ", /*#__PURE__*/React.createElement(Text, {
      fontWeight: 600
    }, allAny), " results matching:"), !isReadOnly && onFilterGroupToggle && /*#__PURE__*/React.createElement(ToggleFilterButton, {
      on: !group.disabled,
      name: "filter group",
      onToggle: () => onFilterGroupToggle(group.groupIndex)
    })), group.filters.map(filter => /*#__PURE__*/React.createElement(Flex, {
      key: `${filter.filterField}-${filter.operator}-${filter.filterValue}`,
      className: classNames('filter', Classes.UI_TEXT, {
        [Classes.DISABLED]: group.disabled || filter.disabled
      }),
      alignItems: "flex-start",
      gap: "4px"
    }, /*#__PURE__*/React.createElement(Flex, {
      gap: "0 4px",
      flexWrap: "wrap",
      py: "4px"
    }, /*#__PURE__*/React.createElement(Text, {
      small: true,
      fontWeight: 600
    }, filter.label), /*#__PURE__*/React.createElement(Text, {
      small: true,
      muted: true
    }, (() => {
      if (isLabelDimension(filter.filterField)) {
        return filter.operator === '<>' ? 'excludes' : 'includes';
      }
      return getOperatorLabel(filter.operator);
    })()), /*#__PURE__*/React.createElement(Text, {
      as: "div",
      small: true,
      fontWeight: 600
    }, (() => {
      const labelData = getLabelForFilter(filter.filterField, filter.filterValue);
      if (labelData) {
        return /*#__PURE__*/React.createElement(Flex, {
          alignItems: "center",
          gap: "4px"
        }, /*#__PURE__*/React.createElement(Box, {
          as: "span",
          width: "8px",
          height: "8px",
          borderRadius: "2px",
          bg: labelData.bg || 'gray400',
          display: "inline-block",
          verticalAlign: "middle"
        }), labelData.name);
      }
      if (CUSTOM_FILTER_REGEX.test(filter.filterValue)) {
        return /*#__PURE__*/React.createElement(VariableTag, {
          name: filter.filterValue.slice(2, -2),
          asTag: false
        });
      }
      return getFilterDisplayValue(filter, getValueLabel, getDimensionLabel);
    })())), !isSingularFilter && !isReadOnly && onFilterItemToggle && /*#__PURE__*/React.createElement(ToggleFilterButton, {
      on: !filter.disabled,
      onToggle: () => onFilterItemToggle(group.groupIndex, filter.filterIndex)
    }))));
  })));
});
export const UdeFilterFormDisplay = observer(({
  type,
  measurementName
}) => {
  const form = useUdeForm();
  const filterGroups = form.getField(`config.data.filters.${type}.filterGroups`);
  const handleFilterGroupToggle = groupIndex => {
    if (filterGroups) {
      const field = filterGroups.at(groupIndex);
      const disabled = field.disabled.getValue();
      field.disabled.setValue(!disabled);
    }
  };
  const handleFilterItemToggle = (groupIndex, filterIndex) => {
    if (filterGroups) {
      const filtersCollection = filterGroups.at(groupIndex).filters;
      const filterField = filtersCollection.at(filterIndex);
      const disabled = filterField.disabled.getValue();
      filterField.disabled.setValue(!disabled);
    }
  };
  const filters = form.getValues().config.data.filters[type];
  if (!filters) {
    return null;
  }
  return /*#__PURE__*/React.createElement(UdeFilterDisplay, {
    filters: filters,
    type: type,
    measurementName: measurementName,
    onFilterGroupToggle: handleFilterGroupToggle,
    onFilterItemToggle: handleFilterItemToggle
  });
});